/*
 * My Services catalog — CargoTrack version of MatNav login/mySubscriptions.do.
 * Each MatNav service maps to the domestic Cognito group(s) that grant it.
 * A service is "current" when the user has any of its groups; otherwise it
 * can be requested (requests are emailed to Customer Registration).
 */

export const MY_SERVICES_PATH = '/d/my-services';

export type MyServicesItem = {
  code: string;
  groups: string[];
  labelKey: string;
  descKey: string;
};

export type MyServicesSection = {
  key: string;
  titleKey: string;
  items: MyServicesItem[];
};

export const MY_SERVICES_SECTIONS: MyServicesSection[] = [
  {
    key: 'services',
    titleKey: 'myServices.sectionServices',
    items: [
      { code: 'IKEST', groups: ['IKEST'], labelKey: 'myServices.svcTrackContainers', descKey: 'myServices.descTrackContainers' },
      { code: 'IKETA', groups: ['IKETA'], labelKey: 'myServices.svcTrackAutos', descKey: 'myServices.descTrackAutos' },
      { code: 'IKEBM', groups: ['IKEBM'], labelKey: 'myServices.svcBookShipments', descKey: 'myServices.descBookShipments' },
      { code: 'IKEBI', groups: ['IKEBI'], labelKey: 'myServices.svcBillingInstructions', descKey: 'myServices.descBillingInstructions' },
      { code: 'IKEAR', groups: ['IKEAR'], labelKey: 'myServices.svcAccountBalances', descKey: 'myServices.descAccountBalances' },
    ],
  },
  {
    key: 'documents',
    titleKey: 'myServices.sectionDocuments',
    items: [
      // MatNav treats Freight Bill as held when either fc (collect) or fp (prepaid) is.
      { code: 'IKEFC', groups: ['IKEFC', 'IKEFP'], labelKey: 'myServices.docFreightBill', descKey: 'myServices.descFreightBill' },
      { code: 'IKEBL', groups: ['IKEBL'], labelKey: 'myServices.docBillOfLading', descKey: 'myServices.descBillOfLading' },
      { code: 'IKEDO', groups: ['IKEDO'], labelKey: 'myServices.docDeliveryOrder', descKey: 'myServices.descDeliveryOrder' },
      { code: 'IKENA', groups: ['IKENA'], labelKey: 'myServices.docNoticeOfArrival', descKey: 'myServices.descNoticeOfArrival' },
    ],
  },
];

const ALL_ITEMS = MY_SERVICES_SECTIONS.flatMap((section) => section.items);

export function findMyServicesItem(code: string): MyServicesItem | null {
  return ALL_ITEMS.find((item) => item.code === code) || null;
}

export type MyServicesSplitSection = {
  key: string;
  titleKey: string;
  current: MyServicesItem[];
  available: MyServicesItem[];
};

/** Split every section into what the user has and what they can request. */
export function buildMyServicesSections(groups: string[] = []): MyServicesSplitSection[] {
  const held = new Set(groups);
  return MY_SERVICES_SECTIONS.map((section) => ({
    key: section.key,
    titleKey: section.titleKey,
    current: section.items.filter((item) => item.groups.some((g) => held.has(g))),
    available: section.items.filter((item) => !item.groups.some((g) => held.has(g))),
  }));
}

export function countMyServices(sections: MyServicesSplitSection[]): { current: number; available: number } {
  return sections.reduce(
    (totals, section) => ({
      current: totals.current + section.current.length,
      available: totals.available + section.available.length,
    }),
    { current: 0, available: 0 },
  );
}

export type ServiceRequestPayload = { services: string[] };

/**
 * Request body for the service-request endpoint: requested codes in catalog
 * order, unknown or already-held codes dropped. The server adds the user's email,
 * name and email, and sends MatNav's "Online Services Request" email to
 * Customer Registration.
 */
export function buildServiceRequestPayload(selectedCodes: string[], groups: string[] = []): ServiceRequestPayload {
  const wanted = new Set(selectedCodes);
  const available = buildMyServicesSections(groups).flatMap((section) => section.available);
  return { services: available.filter((item) => wanted.has(item.code)).map((item) => item.code) };
}
